// Ghost Content API client using the official library
import GhostContentAPI from '@tryghost/content-api';

// Debug environment variables for troubleshooting
console.log('ENV Variables:', {
  GHOST_URL: process.env.REACT_APP_GHOST_URL,
  GHOST_KEY: process.env.REACT_APP_GHOST_CONTENT_API_KEY,
  NODE_ENV: process.env.NODE_ENV
});

// Ghost API configuration 
const api = new GhostContentAPI({
  url: 'https://maincode.ghost.io',
  key: '18960cb31ed708f937ca5ef6ff',
  version: 'v5.0'
});

// Configuration for debug messages
const GHOST_URL = process.env.REACT_APP_GHOST_URL || 'https://maincode.ghost.io'; 
const GHOST_KEY = process.env.REACT_APP_GHOST_CONTENT_API_KEY || '18960cb31ed708f937ca5ef6ff';
const API_VERSION = 'v5.0';

// For development, use a CORS proxy if needed
// Set this to false to disable the CORS proxy even in development
const USE_CORS_PROXY = false; // Disabled for now as it might be causing 403 errors
const CORS_PROXY_URL = 'https://cors-anywhere.herokuapp.com/';

// Get effective URL with proxy if needed
const getEffectiveUrl = () => {
  if (USE_CORS_PROXY && process.env.NODE_ENV === 'development' && !GHOST_URL.includes('localhost')) {
    console.log('Using CORS proxy for development');
    return CORS_PROXY_URL + GHOST_URL;
  }
  return GHOST_URL;
};

const effectiveUrl = getEffectiveUrl();

// Debug final configuration
console.log('Using Ghost configuration:', {
  ORIGINAL_URL: GHOST_URL,
  EFFECTIVE_URL: effectiveUrl,
  GHOST_KEY_LENGTH: GHOST_KEY ? GHOST_KEY.length : 0,
  KEY_SAMPLE: GHOST_KEY ? `${GHOST_KEY.substring(0, 5)}...${GHOST_KEY.substring(GHOST_KEY.length - 5)}` : 'MISSING',
  API_VERSION,
  USE_CORS_PROXY
});

// Create direct API client without CORS proxy for testing
const createClient = (useProxy = false) => {
  const url = useProxy ? CORS_PROXY_URL + GHOST_URL : GHOST_URL;
  return new GhostContentAPI({
    url,
    key: GHOST_KEY,
    version: API_VERSION
  });
};

// Create API client (default)
// const api = createClient(USE_CORS_PROXY);

// Error handler helper function
const handleApiError = (error, endpoint) => {
  console.error(`Error in ${endpoint}:`, error.message);
  
  if (error.response && error.response.status === 403) {
    console.error('403 Forbidden error detected. This means:');
    console.error('1. Your API key may be invalid or expired');
    console.error('2. The Ghost Content API may not be publicly accessible');
    console.error('3. Your Ghost instance might be using private mode');
    console.error('Current API Key:', GHOST_KEY ? `${GHOST_KEY.substring(0, 5)}...${GHOST_KEY.substring(GHOST_KEY.length - 5)}` : 'MISSING');
  } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
    console.error('Network error detected. Please verify:');
    console.error('1. Your Ghost URL is correct:', GHOST_URL);
    console.error('2. Your Ghost instance is running and accessible');
    console.error('3. There are no CORS issues (you might need to enable CORS on your Ghost instance)');
  }
  
  return error.message;
};

// Create wrapper with additional functionality
const ghost = {
  // Posts endpoints
  posts: {
    browse: async (options = {}) => {
      try {
        console.log('Fetching posts with options:', options);
        const posts = await api.posts.browse(options);
        console.log(`Successfully retrieved ${posts.length} posts`);
        return posts;
      } catch (error) {
        handleApiError(error, 'posts.browse');
        return [];
      }
    },
    
    read: async (options = {}) => {
      try {
        if (!options.slug && !options.id) {
          throw new Error('Must provide either slug or id');
        }
        
        console.log('Fetching post details with options:', options);
        const post = await api.posts.read(options);
        console.log('Successfully retrieved post:', post?.title || post?.id);
        return post;
      } catch (error) {
        handleApiError(error, 'posts.read');
        return null;
      }
    }
  },
  
  // Tags endpoints
  tags: {
    browse: async (options = {}) => {
      try {
        console.log('Fetching tags with options:', options);
        const tags = await api.tags.browse(options);
        console.log(`Successfully retrieved ${tags.length} tags`);
        return tags;
      } catch (error) {
        handleApiError(error, 'tags.browse');
        return [];
      }
    },
    
    read: async (options = {}) => {
      try {
        if (!options.slug && !options.id) {
          throw new Error('Must provide either slug or id');
        }
        
        console.log('Fetching tag details with options:', options);
        const tag = await api.tags.read(options);
        console.log('Successfully retrieved tag:', tag?.name || tag?.id);
        return tag;
      } catch (error) {
        handleApiError(error, 'tags.read');
        return null;
      }
    }
  },
  
  // Authors endpoints
  authors: {
    browse: async (options = {}) => {
      try {
        console.log('Fetching authors with options:', options);
        const authors = await api.authors.browse(options);
        console.log(`Successfully retrieved ${authors.length} authors`);
        return authors;
      } catch (error) {
        handleApiError(error, 'authors.browse');
        return [];
      }
    },
    
    read: async (options = {}) => {
      try {
        if (!options.slug && !options.id) {
          throw new Error('Must provide either slug or id');
        }
        
        console.log('Fetching author details with options:', options);
        const author = await api.authors.read(options);
        console.log('Successfully retrieved author:', author?.name || author?.id);
        return author;
      } catch (error) {
        handleApiError(error, 'authors.read');
        return null;
      }
    }
  },
  
  // Settings endpoint
  settings: async () => {
    try {
      console.log('Fetching site settings');
      const settings = await api.settings.browse();
      console.log('Successfully retrieved settings');
      return settings;
    } catch (error) {
      handleApiError(error, 'settings');
      return {};
    }
  },

  // Prefetch related content based on current post
  prefetchRelated: async (currentPost) => {
    if (!currentPost) return;
    
    try {
      console.log('Prefetching related content for post:', currentPost.title || currentPost.id);
      const tasks = [];
      
      // Prefetch content by the same author
      if (currentPost.primary_author && currentPost.primary_author.slug) {
        tasks.push(
          api.posts.browse({
            filter: `primary_author:${currentPost.primary_author.slug}+id:-${currentPost.id}`,
            limit: 3,
            include: ['authors', 'tags']
          })
        );
        
        // Prefetch author details
        tasks.push(
          api.authors.read({ slug: currentPost.primary_author.slug })
        );
      }
      
      // Prefetch content with the same primary tag
      if (currentPost.tags && currentPost.tags.length > 0) {
        tasks.push(
          api.posts.browse({
            filter: `tag:${currentPost.tags[0].slug}+id:-${currentPost.id}`,
            limit: 3,
            include: ['authors', 'tags']
          })
        );
      }
      
      // Run prefetch tasks in parallel but don't block UI
      Promise.all(tasks).catch(err => {
        console.error('Error prefetching related content:', err);
      });
      
      return true;
    } catch (error) {
      console.error('Error prefetching related content:', error);
      return false;
    }
  },

  // Helper method to test connection
  testConnection: async () => {
    try {
      console.log('Testing Ghost API connection...');
      const posts = await api.posts.browse({ limit: 1 });
      console.log('✓ Connection successful! Ghost API is accessible.');
      return { success: true, message: 'Connection successful' };
    } catch (error) {
      console.error('✗ Connection failed:', error.message);
      let troubleshootMsg = '';
      
      if (error.response && error.response.status === 403) {
        troubleshootMsg = `
403 Forbidden: Your request is being rejected. This typically means:
- The API key (${GHOST_KEY ? `${GHOST_KEY.substring(0, 5)}...${GHOST_KEY.substring(GHOST_KEY.length - 5)}` : 'MISSING'}) is invalid or doesn't have proper permissions
- Your Ghost instance might be in private mode and restricting access
- The Content API might not be enabled in your Ghost settings

How to fix:
1. Go to Ghost Admin → Settings → Integrations
2. Create a new Custom Integration or use an existing one
3. Copy the Content API Key (not the Admin API Key)
4. Set it as REACT_APP_GHOST_CONTENT_API_KEY in your environment`;
      } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        troubleshootMsg = `
Network Error: Can't connect to the Ghost API.
- Check if your Ghost URL (${GHOST_URL}) is correct and accessible
- Verify your Content API key is valid
- If developing locally, you may need to enable CORS on your Ghost instance`;
      } else if (error.message.includes('401')) {
        troubleshootMsg = 'Your Content API key appears to be invalid. Get a new key from Ghost Admin > Settings > Integrations.';
      } else if (error.message.includes('404')) {
        troubleshootMsg = 'The Ghost API URL appears to be incorrect. Verify the URL in your environment variables.';
      }
      
      return { 
        success: false, 
        message: error.message,
        troubleshoot: troubleshootMsg
      };
    }
  },
  
  // Test if API key is valid
  validateApiKey: async () => {
    // Try to directly access the API without proxy to validate key
    try {
      console.log('Validating API key...');
      // Create a direct client without proxy for this test
      const directClient = createClient(false);
      
      // First log the key being tested (partially redacted)
      if (!GHOST_KEY || GHOST_KEY === '00000000000000000000000000') {
        console.error('❌ No valid API key provided. Using placeholder key.');
        return {
          valid: false,
          message: 'No API key provided. Please set REACT_APP_GHOST_CONTENT_API_KEY in your environment.'
        };
      }
      
      console.log(`Testing key: ${GHOST_KEY.substring(0, 5)}...${GHOST_KEY.substring(GHOST_KEY.length - 5)}`);
      await directClient.posts.browse({ limit: 1 });
      console.log('✓ API key is valid!');
      return { valid: true, message: 'API key is valid' };
    } catch (error) {
      console.error('❌ API key validation failed:', error.message);
      
      let message = 'Unknown error validating API key';
      if (error.response && error.response.status === 403) {
        message = 'API key is invalid or has insufficient permissions';
      } else if (error.response && error.response.status === 401) {
        message = 'API key is invalid';
      }
      
      return { 
        valid: false, 
        message,
        error: error.message
      };
    }
  }
};

// Run tests when the module loads
Promise.all([
  ghost.testConnection()
    .then(result => {
      if (!result.success) {
        console.error('Ghost API Connection Test Failed:', result.troubleshoot);
      }
    }),
  ghost.validateApiKey()
    .then(result => {
      if (!result.valid) {
        console.error('API Key Validation Failed:', result.message);
      }
    })
])
.catch(err => {
  console.error('Error running connection tests:', err);
});

export default ghost; 